import { catchError, map, tap, Observable, of } from 'rxjs';
import { ComponentDataSourceService } from '../../component-data-source.service';
import { FunctionRegistryService } from '../../../function-registry/function-registry.service';
import { UserDTO } from '../../../../dto/users/user.dto';
import { EducationalPathUserDTO } from '../../../../dto/educational-path/educational-path-user.dto';
import { EducationalPathVersionDTO } from '../../../../dto/educational-path/educational-path-version.dto';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';

/**
 * Format a date string to YYYY-MM-DD for form date inputs
 */
function formatDateForInput(dateString: string | null): string {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return '';
  }
}

/**
 * Format a date string to Danish format (DD-MM-YYYY)
 * @param dateString ISO date string
 * @returns Formatted date string in DD-MM-YYYY format
 */
function formatDanishDate(dateString: string | null): string {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.error('[Date Formatter] Invalid date:', dateString);
      return '';
    }
    
    // Format as DD-MM-YYYY
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
    const year = date.getFullYear();
    
    return `${day}-${month}-${year}`;
  } catch (e) {
    console.error('[Date Formatter] Error formatting date:', e);
    return '';
  }
}

export function educationalPathUsers(componentDataSourceService: ComponentDataSourceService, functionRegistryService: FunctionRegistryService) {
  return {
    name: 'Educational Path Users',
    description: 'Lists all users and their status for a specific educational path',
    template: '<div class="educational-path-users-container" style="margin-bottom: 60px; padding-bottom: 60px;"></div>',
    execute: (params?: LoadDataParams) => {
      
      // Get current path GUID from the URL
      const currentGuid = componentDataSourceService.getCurrentSlug();
      
      if (!currentGuid) {
        console.error('[Educational Path Users] No path GUID found in URL');
        return of({
          items: [],
          totalCount: 0,
          pageSize: 10,
          pageIndex: 0
        });
      }

      // Log the sort parameters specifically
      if (params?.sort) {
      } else {
      }

      // Use the standardized API approach with proper pagination and sorting
      return componentDataSourceService['loadDataWithStandardFiltersGET'](
        'educational_path_users', 
        `/paths/${currentGuid}/users`, 
        params
      ).pipe(
        tap(result => {
        }),
        catchError(error => {
          console.error('Error fetching educational path users:', error);
          return of({
            items: [],
            totalCount: 0,
            pageSize: 10,
            pageIndex: 0
          });
        }),
        map((result: any) => {
          if (!result || !result.items) {
            console.warn('[Educational Path Users] No user data found');
            return {
              items: [],
              totalCount: 0,
              pageSize: result?.pageSize || 10,
              pageIndex: result?.pageIndex || 0
            };
          }

          try {
            const mappedUsers = result.items.map((userPath: EducationalPathUserDTO) => {
              try {
                
                // Get version information and name if available
                // Use the version name exactly as it comes from the backend - no extraction or modification
                const versionName: string | null = userPath.version_name || null;
                
                
                // Set reasonable values for requirement_started_at and maintenance_started_at if they're null
                if (!userPath.requirement_started_at) {
                  userPath.requirement_started_at = userPath.assigned_at;
                }
                
                // For maintenance_started_at, use assigned_at + 1 month if null
                if (!userPath.maintenance_started_at && userPath.assigned_at) {
                  try {
                    // Parse the assigned_at date
                    const assignedDate = new Date(userPath.assigned_at);
                    // Add 1 month to it
                    assignedDate.setMonth(assignedDate.getMonth() + 1);
                    // Format as ISO string
                    userPath.maintenance_started_at = assignedDate.toISOString();
                  } catch (e) {
                    console.error('[Educational Path Users] Error setting maintenance_started_at:', e);
                  }
                }
                
                
                // Combine first and last name
                const fullName = `${userPath.user?.first_name || ''} ${userPath.user?.last_name || ''}`.trim();
                
                const mapped: UserDTO & {
                  full_name: string;
                  commitment_type: string;
                  progress_percentage: number;
                  assigned_at: string | null;
                  version_id: number | null;
                  version_name: string | null;
                  version_end_date: string | null;
                  migrated_to_version_id: number | null;
                  maintenance_minutes_required: number;
                  maintenance_minutes_completed: number;
                  recurrence_window_start: string | null;
                  recurrence_window_end: string | null;
                  recurrence_value: number | null;
                  recurrence_unit: string | null;
                  recurrence_type: string | null;
                  version_info: string;
                  maintenance_progress: number;
                  requirement_started_at: string | null;
                  maintenance_started_at: string | null;
                  requirement_started_at_raw: string;
                  maintenance_started_at_raw: string;
                  maintenance_date: string | null; // Vedligeholdelsesdato - calculated based on recurrence type
                  validation_status: string;
                  registered_percentage: number;
                } = {
                  uuid: userPath.user?.uuid || userPath.guid,
                  first_name: userPath.user?.first_name || '',
                  last_name: userPath.user?.last_name || '',
                  full_name: fullName,
                  user: null,
                  roles: [],
                  organizations_id: userPath.organizations_id,
                  locations: null,
                  status: null,
                  commitment_type: userPath.commitment_type,
                  progress_percentage: userPath.progress_percentage,
                  assigned_at: userPath.assigned_at,
                  version_id: userPath.version_id,
                  version_name: versionName,
                  version_end_date: userPath.version_end_date || null,
                  migrated_to_version_id: userPath.migrated_to_version_id || null,
                  maintenance_minutes_required: userPath.maintenance_minutes_required || 0,
                  maintenance_minutes_completed: userPath.maintenance_minutes_completed || 0,
                  recurrence_window_start: userPath.recurrence_window_start || '',
                  recurrence_window_end: userPath.recurrence_window_end || '',
                  recurrence_value: userPath.recurrence_value || null,
                  recurrence_unit: userPath.recurrence_unit || null,
                  recurrence_type: (userPath as any).recurrence_type || null,
                  version_info: '',
                  maintenance_progress: userPath.maintenance_minutes_required ? 
                    Math.round((userPath.maintenance_minutes_completed / userPath.maintenance_minutes_required) * 100) : 0,
                  // Use the assigned_at date for requirements joining as this is when the user is assigned to the path
                  requirement_started_at: formatDanishDate(userPath.requirement_started_at),
                  // Use the started_at date for maintenance joining as this is typically when maintenance begins
                  maintenance_started_at: formatDanishDate(userPath.maintenance_started_at),
                  // Raw dates for edit-path-user form (YYYY-MM-DD)
                  requirement_started_at_raw: formatDateForInput(userPath.requirement_started_at),
                  maintenance_started_at_raw: formatDateForInput(userPath.maintenance_started_at),
                  // Add DUNA status fields from backend
                  validation_status: userPath.validation_status || 'no_data',
                  registered_percentage: userPath.registered_percentage || 0,
                  // Calculate Vedligeholdelsesdato (next renewal date) based on recurrence type
                  maintenance_date: (() => {
                    const recurrenceType = (userPath as any).recurrence_type || 'calendar';
                    
                    // For per-user recurrence, calculate the next deadline from maintenance_started_at
                    if (recurrenceType === 'per_user') {
                      // Must have maintenance_started_at for per-user calculation
                      if (!userPath.maintenance_started_at || !userPath.recurrence_value || !userPath.recurrence_unit) {
                        return '';
                      }
                      
                      const startDate = new Date(userPath.maintenance_started_at);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      
                      const value = userPath.recurrence_value;
                      const unit = userPath.recurrence_unit.toLowerCase();
                      
                      if (unit === 'full_period') {
                        // No deadline for full period
                        return '';
                      }
                      
                      // Find the next deadline (start of next period) by adding periods from the start date
                      let nextDeadline = new Date(startDate);
                      
                      // Add the first period to get the START of the next period (the deadline)
                      if (unit === 'day' || unit === 'days') {
                        nextDeadline.setDate(nextDeadline.getDate() + value);
                      } else if (unit === 'week' || unit === 'weeks') {
                        nextDeadline.setDate(nextDeadline.getDate() + (value * 7));
                      } else if (unit === 'month' || unit === 'months') {
                        nextDeadline.setMonth(nextDeadline.getMonth() + value);
                      } else if (unit === 'year' || unit === 'years') {
                        nextDeadline.setFullYear(nextDeadline.getFullYear() + value);
                      } else {
                        return '';
                      }
                      
                      // Keep adding periods until we find one that's in the future
                      while (nextDeadline <= today) {
                        // Calculate start of next period (which is the deadline for current period)
                        if (unit === 'day' || unit === 'days') {
                          nextDeadline.setDate(nextDeadline.getDate() + value);
                        } else if (unit === 'week' || unit === 'weeks') {
                          nextDeadline.setDate(nextDeadline.getDate() + (value * 7));
                        } else if (unit === 'month' || unit === 'months') {
                          nextDeadline.setMonth(nextDeadline.getMonth() + value);
                        } else if (unit === 'year' || unit === 'years') {
                          nextDeadline.setFullYear(nextDeadline.getFullYear() + value);
                        }
                      }
                      
                      // Return formatted date
                      return nextDeadline.toLocaleDateString('da-DK');
                    }
                    
                    // For calendar-based, calculate next deadline
                    if (userPath.recurrence_window_end) {
                      const deadline = new Date(userPath.recurrence_window_end);
                      const today = new Date();
                      const diffTime = deadline.getTime() - today.getTime();
                      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                      
                      if (diffDays < 0) {
                        // Calculate next period for calendar-based
                        const currentYear = today.getFullYear();
                        const nextYear = currentYear + 1;
                        const nextDeadline = new Date(`${nextYear}-12-31`);
                        return nextDeadline.toLocaleDateString('da-DK');
                      } else {
                        return deadline.toLocaleDateString('da-DK');
                      }
                    }
                    
                    return '';
                  })()
                };
                
                return mapped;
              } catch (error) {
                console.error('Error processing user path:', userPath, error);
                return null;
              }
            }).filter((user: any) => user !== null);

            
            return {
              items: mappedUsers,
              totalCount: result.totalCount,
              pageSize: result.pageSize,
              pageIndex: result.pageIndex
            };
          } catch (error) {
            console.error('Error converting educational path users:', error);
            console.error('Result data:', result);
            return {
              items: [],
              totalCount: 0,
              pageSize: result?.pageSize || 10,
              pageIndex: result?.pageIndex || 0
            };
          }
        })
      );
    },
    settings: {
      columns: [
        { 
          key: 'full_name', 
          header: 'Fulde navn', 
          type: 'text',
          sortable: true
        },
        { 
          key: 'version_name', 
          header: 'Version', 
          type: 'text',
          sortable: true
        },
        {
          key: 'validation_status',
          header: 'DUNA Status',
          type: 'component',
          componentName: 'duna-validation-badge',
          componentInputs: (user: any) => ({
            status: user.validation_status || 'no_data',
            customTooltip: getValidationTooltip(user.validation_status || 'no_data')
          }),
          sortable: true,
          width: '20%'
        },
        {
          key: 'registered_percentage',
          header: 'Registrering %',
          type: 'text',
          transform: (value: number, row?: any) => {
            if (value === undefined || value === null) return 'N/A';
            const intendedPercentage = Math.min(100, Math.max(0, value)); // Ensure value is between 0-100
            const participatedPercentage = Math.min(100, Math.max(0, row?.progress_percentage || 0));
            
            return `${intendedPercentage}% (${participatedPercentage}%)`;
          },
          sortable: true,
          alignment: 'center',
          width: '160px'
        },
        { 
          key: 'requirement_started_at', 
          header: 'Begyndelsesdato', 
          type: 'text',
          sortable: true
        },
        { 
          key: 'maintenance_started_at', 
          header: 'Afslutningsdato', 
          type: 'text',
          sortable: true
        },
        { 
          key: 'maintenance_date', 
          header: 'Vedligeholdelsesdato', 
          type: 'text',
          sortable: true,
          tooltip: (row: any) => 'Vedligeholdelsesdato baseret på gentagelsestype (per bruger eller kalenderbaseret)'
        },
        { 
          key: 'view_profile',
          header: '',
          type: 'button',
          variant: 'stroked',
          buttonText: 'Se profil',
          buttonColor: 'accent',
          action: (row: UserDTO) => {
            const profileUrl = `/profile/${row.uuid}`;
            window.open(profileUrl, '_blank');
          }
        },
        { 
          key: 'edit',
          header: '',
          type: 'button',
          variant: 'raised',
          buttonText: 'Rediger',
          buttonColor: 'primary',
          action: (row: UserDTO) => {
            const guid = componentDataSourceService.getCurrentSlug();
            functionRegistryService.executeFunction('editPathUser', guid, row.uuid, row);
          }
        }
      ],
      pageable: true,
      sortable: true,
      rowHover: true,
      searchable: true,
      defaultSortColumn: 'full_name',
      defaultSortDirection: 'asc',
      pageSizeOptions: [5, 10, 25, 50]
    }
  }
}

/* -------------------------------------------------------------------------- */
/*  Helper                                                                     */
/* -------------------------------------------------------------------------- */
function getValidationTooltip(status: string): string {
  const lastYear = new Date().getFullYear() - 1;
  
  switch (status) {
    case 'valid':
      return `DUNA krav opfyldt for ${lastYear}`;
    case 'attention':
      return `DUNA kræver opmærksomhed - tjek venligst deltagelse og moduler for ${lastYear}`;
    case 'no_data':
      return `Ingen DUNA data tilgængelig for ${lastYear}`;
    case 'no_paths':
      return 'Ingen uddannelsesveje tildelt';
    default:
      return 'Ukendt DUNA status';
  }
} 